import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useOpenTickets } from '../context/OpenTicketsContext'
import { subscribeKitchen } from '../services/kitchen'
import { describeWeather, fetchRates, fetchWeather, type Rates, type Weather } from '../services/homeInfo'
import { useUpgrade } from '../context/UpgradeContext'
import { planFor } from '../plans'
import { LockIcon } from '../components/icons'
import './HomePage.css'

/**
 * Ana ekran (18 Eki 2026). Girişten sonra ve sol üstteki logoya basınca
 * açılıyor; tablette büyük kutularla tek dokunuşta işe gitmek için
 * (kullanıcı, Menulux'un ana ekranını örnek gösterdi).
 *
 * Solda saat, hava ve kur; sağda dört kutu: Hızlı Satış, Masalar, Paket
 * (yakında), Mutfak. Yalnız kasa yetkisi olan (patron, kasiyer) görüyor;
 * garson sipariş ekranına, mutfak mutfağa gidiyor.
 */
export function HomePage() {
  const { tenant } = useParams()
  const { tenantId, tenantName, admin, isOwner, hasFeature, settingsLoading } = useAuth()
  const askUpgrade = useUpgrade()
  const { openTickets } = useOpenTickets()
  const now = useClock()
  const [weather, setWeather] = useState<Weather | null>(null)
  const [rates, setRates] = useState<Rates | null>(null)
  const [kitchenOpen, setKitchenOpen] = useState<number | null>(null)

  useEffect(() => {
    void fetchWeather().then(setWeather)
    void fetchRates().then(setRates)
    // Kur anlık (20 Eki 2026): ekran açıkken dakikada bir.
    const t = window.setInterval(() => {
      void fetchRates().then((r) => r && setRates(r))
    }, 60_000)
    return () => window.clearInterval(t)
  }, [])

  useEffect(() => {
    if (!tenantId) return
    return subscribeKitchen(tenantId, (tickets) =>
      setKitchenOpen(tickets.filter((t) => t.status === 'open').length),
    )
  }, [tenantId])

  const openTables = useMemo(() => openTickets.filter((t) => t.tableName).length, [openTickets])

  const base = `/${tenant}`
  const greeting = (() => {
    const h = now.getHours()
    if (h < 6) return 'İyi geceler'
    if (h < 12) return 'Günaydın'
    if (h < 18) return 'İyi günler'
    return 'İyi akşamlar'
  })()
  const who = admin?.displayName || admin?.username

  return (
    <div className="home">
      <aside className="home__info">
        <p className="home__hello">
          {greeting}
          {who ? `, ${who}` : ''}
        </p>
        <p className="home__date">
          {now.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long' })}
        </p>
        <p className="home__clock">
          {now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
        </p>

        {weather && (
          <div className="home__weather">
            <WeatherIcon kind={describeWeather(weather.code).kind} day={weather.isDay} />
            <span>
              <b>{Math.round(weather.tempC)}°</b>
              <small>
                {describeWeather(weather.code).text}, {weather.place}
              </small>
            </span>
          </div>
        )}

        {rates && (
          <section className="home__rates" aria-label="Güncel kurlar">
            <h2>Güncel kurlar</h2>
            {rates.list.map((r) => {
              const dir = r.changePct > 0.005 ? 'up' : r.changePct < -0.005 ? 'down' : 'flat'
              return (
                <div key={r.code} className="home__rate">
                  <span className="home__cur">{SYMBOL[r.code]}</span>
                  <span className="home__rate-main">
                    <small>{r.code}</small>
                    <b>{r.tl.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</b>
                  </span>
                  <span className={`home__chg is-${dir}`}>
                    {dir === 'up' ? '▲' : dir === 'down' ? '▼' : '•'} %
                    {Math.abs(r.changePct).toLocaleString('tr-TR', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                  </span>
                </div>
              )
            })}
            <p className="home__src">
              {rates.source} kuru, satış, {new Date(rates.date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </section>
        )}

        <p className="home__tenant">{tenantName}</p>
      </aside>

      <nav className={`home__tiles ${isOwner ? '' : 'home__tiles--three'}`} aria-label="Ana ekran">
        <Tile to={base} tab="gelal" tone="orange" icon={<SaleIcon />} title="Hızlı Satış" hint="Gel-al, kasada hemen öde" />
        <Tile
          to={base}
          tab="masa"
          tone="blue"
          icon={<TablesIcon />}
          title="Masalar"
          hint="Açık masalar ve adisyonlar"
          count={openTables}
        />
        <Tile tone="violet" icon={<ScooterIcon />} title="Paket" hint="Eve teslim siparişler" soon />
        {/* Kasiyerde yok (kullanıcı, 21 Eki 2026). */}
        {/* Paketinde yoksa kilitli (29 Eki 2026): basınca yükseltme penceresi. */}
        {isOwner && !hasFeature('mutfak') && !settingsLoading && (
          <Tile
            tone="green"
            icon={<ChefIcon />}
            title="Mutfak"
            hint="Hazırlanan siparişler"
            locked={planFor('mutfak')?.name}
            onLocked={() => askUpgrade('mutfak')}
          />
        )}
        {isOwner && hasFeature('mutfak') && (
          <Tile
            to={`${base}/mutfak`}
            tone="green"
            icon={<ChefIcon />}
            title="Mutfak"
            hint="Hazırlanan siparişler"
            count={kitchenOpen ?? undefined}
          />
        )}
      </nav>
    </div>
  )
}

const SYMBOL: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' }

/** Dakikada bir değil, her 10 sn'de: dakika döndüğü an saat geç kalmasın. */
function useClock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 10_000)
    return () => window.clearInterval(t)
  }, [])
  return now
}

function Tile({
  to,
  tab,
  tone,
  icon,
  title,
  hint,
  count,
  soon,
  locked,
  onLocked,
}: {
  to?: string
  /** Satış ekranı hangi sekmeyle açılsın. */
  tab?: 'gelal' | 'masa'
  tone: 'orange' | 'blue' | 'violet' | 'green'
  icon: ReactNode
  title: string
  hint: string
  count?: number
  soon?: boolean
  /** Paketinde yok: hangi pakette olduğu (rozette yazar). */
  locked?: string
  onLocked?: () => void
}) {
  const body = (
    <>
      <span className="home__icon">{icon}</span>
      <span className="home__label">
        <b>{title}</b>
        <small>{hint}</small>
      </span>
      {count !== undefined && count > 0 && <span className="home__count">{count}</span>}
      {soon && <span className="home__soon">Yakında</span>}
      {locked && (
        <span className="home__soon home__soon--lock">
          <LockIcon />
          {locked} paketinde
        </span>
      )}
    </>
  )
  if (locked) {
    return (
      <button type="button" className={`home__tile is-${tone} is-soon is-locked`} onClick={onLocked}>
        {body}
      </button>
    )
  }
  if (soon || !to) {
    return (
      <div className={`home__tile is-${tone} is-soon`} aria-disabled="true">
        {body}
      </div>
    )
  }
  return (
    <Link className={`home__tile is-${tone}`} to={tab ? `${to}?sekme=${tab}` : to}>
      {body}
    </Link>
  )
}

/* ---- Kutu ve hava simgeleri: uygulamanın çizgi simgeleriyle aynı dil ---- */

const svg = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

function SaleIcon() {
  return (
    <svg {...svg}>
      <path d="M5 8h14l-1.2 11.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8z" />
      <path d="M9 8V6.5a3 3 0 0 1 6 0V8" />
      <path d="M10 13.5l1.6 1.6L14.5 12" />
    </svg>
  )
}

function TablesIcon() {
  return (
    <svg {...svg}>
      <ellipse cx="12" cy="9" rx="8" ry="3" />
      <path d="M12 12v8M8.5 20h7" />
      <path d="M4 9v2.5M20 9v2.5" />
    </svg>
  )
}

function ScooterIcon() {
  return (
    <svg {...svg}>
      <circle cx="6" cy="17.5" r="2.5" />
      <circle cx="18" cy="17.5" r="2.5" />
      <path d="M8.5 17.5h7l-2-6H9" />
      <path d="M13.5 11.5L15 6h2.5" />
      <path d="M15.5 17.5l1.4-5.6" />
      <rect x="3" y="7" width="6" height="4.5" rx="1" />
    </svg>
  )
}

function ChefIcon() {
  return (
    <svg {...svg}>
      <path d="M7 14.5a4 4 0 0 1-1-7.8A4.5 4.5 0 0 1 12 4a4.5 4.5 0 0 1 6 2.7 4 4 0 0 1-1 7.8" />
      <path d="M7 14v5.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V14" />
      <path d="M7 17h10" />
    </svg>
  )
}

function WeatherIcon({ kind, day }: { kind: ReturnType<typeof describeWeather>['kind']; day: boolean }) {
  const sun = day ? (
    <g className="home__sun">
      <circle cx="9" cy="9" r="3.2" />
      <path d="M9 2.5v1.3M9 14.2v1.3M2.5 9h1.3M14.2 9h1.3M4.4 4.4l.9.9M12.7 12.7l.9.9M4.4 13.6l.9-.9M12.7 5.3l.9-.9" />
    </g>
  ) : (
    <path className="home__sun" d="M12.5 11.5A5 5 0 0 1 7 4a5.5 5.5 0 1 0 5.5 7.5z" />
  )
  const cloud = <path className="home__cloud" d="M8 20h9.5a3.5 3.5 0 0 0 .4-7 5 5 0 0 0-9.6-1A4 4 0 0 0 8 20z" />
  return (
    <svg {...svg} viewBox="0 0 24 24" className={`home__wx is-${kind}`}>
      {(kind === 'clear' || kind === 'partly') && sun}
      {kind !== 'clear' && cloud}
      {kind === 'rain' && <path d="M10 21.5l-.6 1.5M14 21.5l-.6 1.5M18 21.5l-.6 1.5" />}
      {kind === 'snow' && <path d="M10 22h.01M14 22h.01M18 22h.01" />}
      {kind === 'storm' && <path d="M13 20l-1.5 2.5h3L13 24" />}
      {kind === 'fog' && <path d="M5 22h14" />}
    </svg>
  )
}
